import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ArtworkCard from "@/components/ArtworkCard";
import { ShieldCheck, ArrowRight, CreditCard, Truck, PackageCheck, BadgeEuro, Building2, Palette, Sparkles } from "lucide-react";

export default function Landing() {
  const [artworks, setArtworks] = useState([]);
  useEffect(() => {
    api.get("/artworks", { params: { status: "available" } })
      .then((r) => setArtworks(r.data.slice(0, 8)))
      .catch((e) => console.error("Failed to load featured artworks:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[calc(100vh-6rem)] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="/gallery-hero.jpg" alt="Inside an elegant art gallery"
            className="w-full h-full object-cover brightness-110" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/25 to-transparent" />
        </div>
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl hero-fade-up">
            <div className="overline-badge flex items-center gap-2 text-[var(--gold)]"><ShieldCheck className="w-4 h-4" /> Global Marketplace</div>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight mt-5 text-white">
              Where artists meet collectors, worldwide.
            </h1>
            <p className="text-base sm:text-lg text-stone-200 leading-relaxed mt-6 max-w-xl">
              ARTFUTUR sells original art on behalf of artists everywhere. Buy with confidence —
              the artist is paid only once your artwork is delivered.
            </p>
            <div className="flex flex-wrap gap-3 mt-9">
              <Link to="/gallery" data-testid="hero-explore-btn"
                className="bg-white text-[var(--text-primary)] hover:bg-[var(--gold)] hover:text-white transition-colors px-7 py-3.5 rounded-full text-sm font-semibold flex items-center gap-2">
                Explore the gallery <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/sell" data-testid="hero-sell-btn"
                className="px-7 py-3.5 rounded-full text-sm font-semibold border border-white/70 text-white hover:bg-white hover:text-[var(--text-primary)] transition-colors">
                Sell your art
              </Link>
            </div>
            <div className="mt-10 inline-flex items-center gap-3 glass-dark rounded-full px-5 py-2.5">
              <span className="text-[0.65rem] font-mono uppercase tracking-[0.2em] text-[var(--gold)]">Now Exhibiting</span>
              <span className="text-white text-sm">A curated global collection</span>
            </div>
          </div>
        </div>
      </section>

      {/* ESCROW STRIP */}
      <section className="bg-[var(--bg-dark)] text-white relative grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center max-w-2xl mx-auto">
            <div className="overline-badge">How ARTFUTUR Works</div>
            <h2 className="font-serif text-3xl sm:text-4xl mt-4">Simple to buy. Secure to sell.</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {[
              [CreditCard, "1. Pay at purchase", "The buyer pays when they buy. The payment is held safely by ARTFUTUR."],
              [BadgeEuro, "2. We hold the funds", "Your money stays protected in our account while the artist prepares the work."],
              [Truck, "3. Artist ships", "The artist ships with tracking — the sooner they deliver, the sooner they're paid."],
              [PackageCheck, "4. Released on delivery", "Once delivery is confirmed, the artist is paid. Simple, secure, worldwide."],
            ].map(([Icon, t, d]) => (
              <div key={t} className="glass-dark rounded-xl p-6">
                <div className="w-11 h-11 rounded-full bg-[var(--gold)]/20 flex items-center justify-center text-[var(--gold)]">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-xl mt-4">{t}</h3>
                <p className="text-sm text-stone-400 mt-2 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline-badge">Curated Selection</div>
            <h2 className="font-serif text-3xl sm:text-4xl mt-3">Featured works</h2>
          </div>
          <Link to="/gallery" className="text-sm font-medium text-[var(--gold)] flex items-center gap-1 hover:gap-2 transition-all">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {artworks.map((a) => <ArtworkCard key={a.id} art={a} />)}
        </div>
      </section>

      {/* BUYER SEGMENTS */}
      <section className="bg-[var(--bg-secondary)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-3 gap-8">
          {[
            [Palette, "Collectors & Retail", "Discover original works from emerging and established artists, protected end-to-end.", "/gallery"],
            [Building2, "Hospitality & Business", "Curate art for hotels, gyms and venues at scale, with dedicated trade support.", "/trade"],
            [Sparkles, "Interior Designers", "Curate art for client projects with dedicated trade support and preferential pricing.", "/trade"],
          ].map(([Icon, t, d, link]) => (
            <Link to={link} key={t}
              className="bg-white rounded-2xl p-8 border border-[var(--border-subtle)] hover:border-[var(--gold)] transition-colors group">
              <div className="w-12 h-12 rounded-full bg-[var(--gold-light)] flex items-center justify-center text-[var(--gold-hover)]">
                <Icon className="w-5 h-5" />
              </div>
              <h3 className="font-serif text-2xl mt-5">{t}</h3>
              <p className="text-sm text-[var(--text-secondary)] mt-3 leading-relaxed">{d}</p>
              <span className="inline-flex items-center gap-1 text-sm text-[var(--gold)] font-medium mt-4 group-hover:gap-2 transition-all">
                Learn more <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
